import { success } from './../../../services/svg/icons/success';
import { Component, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Apollo } from "apollo-angular";
import { GET_GAZETI_PREVIEWS } from "../../../store/entities/epaper-management/epaper.graphql";
import { CHECK_GAZETI_ACCESS } from "../landing.graphql";
import { AuthUserProfile } from "../../../store/entities/uaa/auth-user.model";
import { NotificationService } from "../../../services/notification.service";
import { UserManagementService } from "../../../services/user-management.service";
import { StorageService } from "../../../services/storage.service";
interface GazetiItem {
  uuid: string;
  title: string;
  description: string | null;
  publicationNumber: string;
  publicationDate: string;
  status: string;
  totalPages: number;
  thumbnailUrl: string;
  thumbnailAltText: string | null;
  thumbnailFileName: string | null;
}
@Component({
  selector: "app-corporate-today-trends",
  templateUrl: "./corporate-today-trends.component.html",
  styleUrl: "./corporate-today-trends.component.scss",
  standalone: false,
})
export class CorporateTodayTrendsComponent implements OnInit {
  private notificationService: NotificationService =
    inject(NotificationService);
  private uaaService: UserManagementService = inject(UserManagementService);
  private storageService = inject(StorageService);
  allNews: GazetiItem[] = [];
  isLoadingNews = false;
  isLoadingMore = false;
  showAuthModal = false;
  selectedGazetiUuid: string | null = null;

  currentIndex = 0;
  itemsPerPage = 12;
  currentPage = 1;
  hasNextPage = true;
  autoSlideInterval: any = null;

  startDate: string | null = null;
  endDate: string | null = null;

  selectedDate: string | null = null;

  constructor(private router: Router, private apollo: Apollo) {}

  ngOnInit() {
    this.loadGazeti(1);
    this.startAutoSlide();
  }

  onDateChange(date: Date | null) {
    if (!date) return;

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    this.selectedDate = `${yyyy}-${mm}-${dd}`;

    // reset carousel & pagination
    this.currentIndex = 0;
    this.currentPage = 1;
    this.hasNextPage = true;
    this.allNews = [];

    this.loadGazeti(1);
  }

  loadGazeti(page: number) {
    if (page < 1 || (!this.hasNextPage && page > 1)) return;

    if (page === 1) {
      this.isLoadingNews = true;
    } else {
      this.isLoadingMore = true;
    }
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    this.apollo
      .query<any>({
        query: GET_GAZETI_PREVIEWS,
        variables: {
          filtering: {
            pageNumber: 0,
            itemsPerPage: this.itemsPerPage,
            gazetiStatus: "PUBLISHED",
            ...(this.selectedDate && {
              publicationDate: this.selectedDate,
            }),
            //  publicationDate: todayString,
          },
        },
        fetchPolicy: "network-only",
      })
      .subscribe({
        next: (result) => {
          const response = result.data?.getGazetiPreviews;

          if (response?.data && response.data.length > 0) {
            if (page === 1) {
              this.allNews = response.data;
            } else {
              this.allNews = [...this.allNews, ...response.data];
            }

            this.hasNextPage =
              response.pageInfo?.hasNextPage ??
              response.data.length === this.itemsPerPage;
            this.currentPage = page;
          } else {
            this.hasNextPage = false;
          }

          this.isLoadingNews = false;
          this.isLoadingMore = false;
        },
        error: (error) => {
          console.error("Error loading gazeti:", error);
          this.isLoadingNews = false;
          this.isLoadingMore = false;
          this.hasNextPage = false;
        },
      });
  }

  nextSlide() {
    const visibleCount = this.getVisibleItemsCount();
    if (this.currentIndex + visibleCount >= this.allNews.length) {
      // Load more if near end
      if (this.hasNextPage && !this.isLoadingMore) {
        this.loadGazeti(this.currentPage + 1);
      }
    } else {
      this.currentIndex += 1;
    }
  }

  previousSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
  }

  getVisibleItemsCount(): number {
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  }

  get slideWidth(): number {
    return 100 / this.getVisibleItemsCount();
  }

  get hasNextItems(): boolean {
    return (
      this.currentIndex + this.getVisibleItemsCount() < this.allNews.length ||
      this.hasNextPage
    );
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  onImageError(event: any) {
    const imgElement = event.target as HTMLImageElement;

    if (imgElement.src.includes("default-newspaper.jpg")) {
      return;
    }

    imgElement.src = "assets/images/default-newspaper.jpg";

    imgElement.onerror = null;
  }
  private async loadUserProfile(): Promise<AuthUserProfile | null> {
    try {
      const authUser = await this.uaaService.getUserProfileAndRoles();
      console.log("User profile loaded", authUser);
      return authUser;
    } catch (error: any) {
      this.notificationService.errorMessage(
        error?.message || "Failed to load user profile"
      );
      return null;
    }
  }
async onReadMore(gazeti: GazetiItem) {
  const gazetiUuid = gazeti.uuid;

  // Must be logged in
  const authUser = await this.loadUserProfile();
  if (!authUser) {
    await this.router.navigate(['/landing']);
    return;
  }

  const userType = (authUser.userType || '').toUpperCase();
  const roles = (authUser.role || []).map((r: any) => r.name?.toUpperCase());
  const hasCorporateReaderRole = roles.includes('CORPORATE_READER');
  const isCorporateUser = userType === 'CORPORATE';
  const isPersonalOrAdmin = userType === 'PERSONAL' || userType === 'ADMIN'; // Add more types if needed

  try {
    const result = await this.apollo
      .query<{
        checkGazetiAccess: {
          response: { status: boolean; message: string };
          data: { hasAccess: boolean; hasSubscription: boolean };
        };
      }>({
        query: CHECK_GAZETI_ACCESS,
        variables: { gazetiUuid },
        fetchPolicy: 'network-only',
      })
      .toPromise();

    const accessResult = result?.data?.checkGazetiAccess;
    const responseOk = accessResult?.response?.status === true;
    const hasAccess = accessResult?.data?.hasAccess === true;
    const hasSubscription = accessResult?.data?.hasSubscription === true;
    const hasFullAccess = responseOk && hasAccess && hasSubscription;

   
    const redirectToSubscription = () => {
    
      localStorage.setItem('pendingGazetiUuid', gazetiUuid);

      this.notificationService.successMessage(
        'You need an active subscription to read this gazeti. Redirecting to plans...'
      );

   
      setTimeout(() => {
        this.router.navigate(['/subscription']);
      }, 1800);
    };

    // =====================================================
    // CORPORATE USERS - Strict rules
    // =====================================================
    if (isCorporateUser) {
      if (hasFullAccess && hasCorporateReaderRole) {
        await this.router.navigate(['/gazeti-preview', gazetiUuid]);
      } else {
        this.notificationService.errorMessage(
          'You do not have permission to read this gazeti. Please contact your corporate administrator.'
        );
      }
      return;
    }

    // =====================================================
    // PERSONAL, ADMIN, OR ANY OTHER USER TYPE
    // Can subscribe themselves → send to subscription page
    // =====================================================
    if (isPersonalOrAdmin || !isCorporateUser) {
      if (hasFullAccess) {
        await this.router.navigate(['/gazeti-preview', gazetiUuid]);
      } else {
        redirectToSubscription();
      }
      return;
    }

    // =====================================================
    // FALLBACK (shouldn't reach here)
    // =====================================================
    await this.router.navigate(['/innerlanding']);

  } catch (error) {
    console.error('Error checking gazeti access:', error);
    this.notificationService.errorMessage('Unable to check access. Please try again.');
    await this.router.navigate(['/landing']);
  }
}


  closeModal() {
    this.showAuthModal = false;
    this.selectedGazetiUuid = null;
  }

  goToRegister() {
    this.router.navigate(["/register"], {
      state: { intendedGazetiUuid: this.selectedGazetiUuid },
    });
    this.closeModal();
  }

  goToLogin() {
    this.router.navigate(["/login"], {
      state: { intendedGazetiUuid: this.selectedGazetiUuid },
    });
    this.closeModal();
  }
  ngOnDestroy() {
    this.stopAutoSlide(); // ← Add this to clean up
  }

  startAutoSlide() {
    this.autoSlideInterval = setInterval(() => {
      this.nextSlide();
    }, 6000); // Change 6000 to any milliseconds (6000 = 6 seconds)
  }

  stopAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }

  // Optional: Pause auto-slide when user hovers (better UX)
  onCarouselHover(isHovering: boolean) {
    if (isHovering) {
      this.stopAutoSlide();
    } else {
      this.startAutoSlide();
    }
  }
}
